import { Divider, Drawer, Stack } from '@mui/material';
import { type MouseEvent } from 'react';

import { useSideNavigationAnimation } from './Animation';
import { SidebarNavigationBottomSection } from './Sections/BottomSection';
import { SidebarNavigationMiddleSection } from './Sections/MiddleSection';
import { SidebarNavigationTopSection } from './Sections/TopSection';
import { SidebarNavigationRouteSectionsProvider } from './Sections/lib/sidebarRoutes';
import { NavigationDrawerTheme } from './Theme';
import { SIDEBAR_NAVIGATION_COLLAPSED_WIDTH, SIDEBAR_NAVIGATION_DRAWER_WIDTH } from './constants';
import { SideNavigationMode, SideNavigationState, useSideNavigationModeContext } from './context';

function InternalNavigationDrawer() {
  const { sideNavigationMode, sideNavigationState, setIsNavigationHoveringIfAfterDelay } =
    useSideNavigationModeContext();

  const { sideNavigationEnteringAndLeavingAnimation } = useSideNavigationAnimation();

  const isOverlayOnHover = sideNavigationMode === SideNavigationMode.OverlayOnHover;

  return (
    <Drawer
      open
      variant="permanent"
      sx={{
        flexShrink: 0,
        width: isOverlayOnHover
          ? SIDEBAR_NAVIGATION_COLLAPSED_WIDTH
          : SIDEBAR_NAVIGATION_DRAWER_WIDTH,
        ...sideNavigationEnteringAndLeavingAnimation,
        '& .MuiDrawer-paper': {
          width:
            sideNavigationState === SideNavigationState.Expanded
              ? SIDEBAR_NAVIGATION_DRAWER_WIDTH
              : SIDEBAR_NAVIGATION_COLLAPSED_WIDTH,
          ...sideNavigationEnteringAndLeavingAnimation,
        },
      }}
      slotProps={{
        paper: {
          sx: {
            overflow: 'hidden',
            paddingY: 0.5,
            background: 'linear-gradient(180deg, #030A33 0%, #00044B 100%)',
            boxShadow:
              isOverlayOnHover && sideNavigationState === SideNavigationState.Expanded ? 6 : 0,
          },
        },
      }}
      onMouseLeave={() => setIsNavigationHoveringIfAfterDelay(false)}
      onMouseEnter={(event: MouseEvent) => {
        if (event.buttons === 0) {
          setIsNavigationHoveringIfAfterDelay(true);
        }
      }}
    >
      <SidebarNavigationRouteSectionsProvider>
        <Stack
          role="navigation"
          sx={{ height: '100%', gap: 0.5, borderRadius: 0, overflowX: 'hidden' }}
          divider={<Divider />}
          tabIndex={sideNavigationState === SideNavigationState.Collapsed ? -1 : 0}
        >
          <SidebarNavigationTopSection />
          <SidebarNavigationMiddleSection />
          <SidebarNavigationBottomSection />
        </Stack>
      </SidebarNavigationRouteSectionsProvider>
    </Drawer>
  );
}

export function NavigationDrawer() {
  return (
    <NavigationDrawerTheme>
      <InternalNavigationDrawer />
    </NavigationDrawerTheme>
  );
}
